import type { ServerModule } from '../ServerWorkflowType';

export interface ModuleVersionGroup {
  latest: ServerModule;
  versions: ServerModule[];
}

const parseVersion = (version: string) => {
  const match = version.match(/^(\d+(?:\.\d+)*)(.*)$/);
  if (!match) {
    return { numeric: [], suffix: version };
  }
  const [, numericPart, suffix] = match;
  return { numeric: numericPart.split('.').map(Number), suffix };
};

const compareVersionsDescending = (versionA: string, versionB: string) => {
  const parsedA = parseVersion(versionA);
  const parsedB = parseVersion(versionB);
  const maxLength = Math.max(parsedA.numeric.length, parsedB.numeric.length);

  for (let index = 0; index < maxLength; index += 1) {
    const diff =
      (parsedB.numeric[index] ?? 0) - (parsedA.numeric[index] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }

  return parsedB.suffix.localeCompare(parsedA.suffix);
};

export const groupModulesByVersion = (
  modules: ServerModule[]
): ModuleVersionGroup[] => {
  const modulesByTextId = new Map<string, ServerModule[]>();

  modules.forEach((module) => {
    const existingVersions = modulesByTextId.get(module.textId) ?? [];
    modulesByTextId.set(module.textId, [...existingVersions, module]);
  });

  const groups = Array.from(modulesByTextId.values()).map((versions) => {
    const sortedVersions = [...versions].sort((moduleA, moduleB) =>
      compareVersionsDescending(moduleA.version, moduleB.version)
    );
    return { latest: sortedVersions[0], versions: sortedVersions };
  });

  return groups.sort((groupA, groupB) =>
    groupA.latest.name.localeCompare(groupB.latest.name, undefined, {
      sensitivity: 'base',
    })
  );
};
